// Sidebar.jsx
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/ApiConfig';
import RelatorioFiltroModal from './modals/RelatorioFiltroModal';
import '../css/Sidebar.css';

const Sidebar = ({ isSidebarOpen, toggleSidebar, sidebarRef }) => {
  const [user, setUser] = useState(null);
  const [escola, setEscola] = useState(null);
  const [vinculo, setVinculo] = useState(null);
  const [drivePath, setDrivePath] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [showRelatorioModal, setShowRelatorioModal] = useState(false);
  const [relatorioTipo, setRelatorioTipo] = useState('');
  const [programas, setProgramas] = useState([]);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [errorRelatorio, setErrorRelatorio] = useState(null);
  const adminSubmenuRef = useRef(null);
  const financeiroSubmenuRef = useRef(null);
  const relatoriosSubmenuRef = useRef(null);
  const bancoSubmenuRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const storedOptions = localStorage.getItem('vinculo');
  const parsedOptions = storedOptions ? JSON.parse(storedOptions) : [];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    const escolaData = localStorage.getItem('escola');
    if (escolaData) {
      setEscola(JSON.parse(escolaData));
    }
    const vinculoData = localStorage.getItem('vinculo');
    if (vinculoData) {
      setVinculo(JSON.parse(vinculoData));
    }
  }, []);

  // Buscar o drivePath da escola selecionada
  useEffect(() => {
    const fetchDrivePath = async () => {
      setDrivePath(null);
      try {
        const escolaData = localStorage.getItem('escola');
        if (escolaData) {
          const escolaObj = JSON.parse(escolaData);
          if (escolaObj && escolaObj.cnpj) {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL.DRIVE_PATH}/${escolaObj.cnpj}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && res.data.path) {
              setDrivePath(res.data.path);
            } else {
              setDrivePath(null);
            }
          }
        }
      } catch (err) {
        setDrivePath(null);
      }
    };
    fetchDrivePath();
  }, [escola]);

  const toggleMenu = (menuName) => {
    if (!isSidebarOpen) {
      toggleSidebar();
    }

    setExpandedMenus(prev => {
      const isOpening = !prev[menuName];
      if (menuName === 'saldos') {
        return {
          pddeFinanceiro: true,
          saldos: isOpening
        };
      }

      if (menuName === 'consultasFnde') {
        return {
          pddeFinanceiro: true,
          consultasFnde: isOpening
        };
      }
      if (menuName === 'banco') {
        return {
          admin: true,
          banco: isOpening
        };
      }
      if (menuName === 'ajustes') {
        return {
          pddeFinanceiro: true,
          ajustes: isOpening
        };
      }

      if (menuName === 'prestacao') {
        return {
          relatorios: true,
          prestacao: isOpening
        };
      }
      if (menuName === 'modelos') {
        return {
          relatorios: true,
          modelos: isOpening
        };
      }

      if (isOpening) {
        return { [menuName]: true };
      }

      return {};
    });
  };
  

  const isActive = (path) => location.pathname === path;

  // Considera rota ativa quando inicia com o path (útil para submenus)
  const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(path);

  // Verifica se qualquer rota de Saldos está ativa
  const isSaldosActive = ['/extratos','/saldos','/saldos-investimento','/saldos-conta','/saldo-anterior']
    .some(p => location.pathname.startsWith(p));

  // Verifica se qualquer rota de Consultas FNDE está ativa
  const isConsultasFndeActive = ['/saldo-fnde','/saldo-fnde-gestao','/saldo-fnde-mensal','/dados-fnde']
    .some(p => location.pathname.startsWith(p));

  const isComparativoFndeActive = ['/comparativo-sres-saldo-fnde']
    .some(p => location.pathname.startsWith(p));

  // Verifica se qualquer rota de Ajustes está ativa
  const isAjustesActive = ['/compensacoes','/devolucoes','/redistribuicoes','/recursos-proprios','/srea']
    .some(p => location.pathname.startsWith(p));

  // Ref para guardar posições de scroll dos submenus entre navegações
  const savedScrollRef = useRef({ admin: 0, financeiro: 0, relatorios: 0, banco: 0 });

  // Rastreia scroll continuamente via event listeners (sem depender de timing de clique)
  useEffect(() => {
    const pairs = [
      [adminSubmenuRef, 'admin'],
      [financeiroSubmenuRef, 'financeiro'],
      [relatoriosSubmenuRef, 'relatorios'],
      [bancoSubmenuRef, 'banco'],
    ];
    const cleanups = pairs.map(([ref, key]) => {
      const el = ref.current;
      if (!el) return null;
      const handler = () => { savedScrollRef.current[key] = el.scrollTop; };
      el.addEventListener('scroll', handler, { passive: true });
      return () => el.removeEventListener('scroll', handler);
    });
    return () => cleanups.forEach(c => c && c());
  }, [expandedMenus, isSidebarOpen]);

  // Restaura scroll antes do paint (useLayoutEffect = síncrono, sem flicker)
  useLayoutEffect(() => {
    if (adminSubmenuRef.current) adminSubmenuRef.current.scrollTop = savedScrollRef.current.admin;
    if (financeiroSubmenuRef.current) financeiroSubmenuRef.current.scrollTop = savedScrollRef.current.financeiro;
    if (relatoriosSubmenuRef.current) relatoriosSubmenuRef.current.scrollTop = savedScrollRef.current.relatorios;
    if (bancoSubmenuRef.current) bancoSubmenuRef.current.scrollTop = savedScrollRef.current.banco;
  }, [location.pathname]);


  // Load programas when Sidebar mounts (for report filters)
  useEffect(() => {
    const fetchProgramas = async () => {
      try {
        const token = localStorage.getItem('token');
        const cnpj = localStorage.getItem('cnpj');
        const ano = localStorage.getItem('ano');

        const [progRes, sresRes] = await Promise.all([
          axios.get(`${API_BASE_URL.PROGRAMA}/all`, { headers: { Authorization: `Bearer ${token}` } }),
          cnpj && ano
            ? axios.get(`${API_BASE_URL.SRES}/search`, { params: { idEscola: cnpj, ano: Number(ano) }, headers: { Authorization: `Bearer ${token}` } })
            : Promise.resolve({ data: [] }),
        ]);

        const todosProgramas = progRes.data || [];
        const sresData = sresRes.data || [];
        const idProgramasComSres = new Set(sresData.map(s => String(s.idPrograma)));

        setProgramas(todosProgramas.filter(p => idProgramasComSres.has(String(p.id))));
      } catch {
        setProgramas([]);
      }
    };
    fetchProgramas();
  }, []);

  // Generic handler for report generation
  const handleAbrirRelatorio = (tipo) => {
    // Relatórios sem necessidade de filtro de programa: gera diretamente
    if (tipo === 'CapaGeral' || tipo === 'PlanPesqPrecos01' || tipo === 'PlanPesqPrecos02') {
      setErrorRelatorio(null);
      handleGerarRelatorio({ tipo });
      return;
    }
    setRelatorioTipo(tipo);
    setShowRelatorioModal(true);
    setErrorRelatorio(null);
  };

  const handleGerarRelatorio = async ({ tipo, idPrograma }) => {
    setLoadingRelatorio(true);
    setErrorRelatorio(null);
    try {
      const token = localStorage.getItem('token');
      const escola = JSON.parse(localStorage.getItem('escola') || '{}');
      const ano = localStorage.getItem('ano');
      let endpoint = '';
      let filename = '';
      // Add more cases here for other reports
      if (tipo === 'Demonstrativos') {
        endpoint = `${API_BASE_URL.DEMONSTRATIVO}/preencher`;
        filename = 'DEMONSTRATIVOS.docx';
      } else if (tipo === 'CapaGeral') {
        endpoint = `${API_BASE_URL.RELATORIO}/capaGeral`;
        filename = `CAPA_GERAL_${ano}.docx`;
      } else if (tipo === 'PlanPesqPrecos01') {
        endpoint = `${API_BASE_URL.RELATORIO}/planPesqPrecos01`;
        filename = `PLAN_PESQ_PRECOS_01.docx`;
      } else if (tipo === 'PlanPesqPrecos02') {
        endpoint = `${API_BASE_URL.RELATORIO}/planPesqPrecos02`;
        filename = `PLAN_PESQ_PRECOS_02.docx`;
      } else if (tipo === 'CapaPrograma') {
        endpoint = `${API_BASE_URL.RELATORIO}/capaPrograma`;
        filename = `CAPA_PROGRAMA_${ano}.docx`;
      } else {
        setErrorRelatorio('Tipo de relatório não suportado.');
        setLoadingRelatorio(false);
        return;
      }
      // Monta params dinamicamente (nem todo relatório usa idPrograma)
      let params = { idEscola: escola.cnpj, ano };
      if (tipo === 'Demonstrativos') params = { idPrograma, idEscola: escola.cnpj, ano };
      if (tipo === 'CapaPrograma') params = { idPrograma: idPrograma || null, idEscola: escola.cnpj, ano };
      if (tipo === 'PlanPesqPrecos01' || tipo === 'PlanPesqPrecos02') params = { idEscola: escola.cnpj };
      const response = await axios.post(
        endpoint,
        null,
        { params, responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setShowRelatorioModal(false);
    } catch (err) {
      setErrorRelatorio('Erro ao gerar relatório.');
    } finally {
      setLoadingRelatorio(false);
    }
  };

  // Auto-expandir menus com base na rota atual
  useEffect(() => {
    const newExpandedMenus = { ...expandedMenus };
    
    if (
      location.pathname.startsWith('/pessoas') ||
      location.pathname.startsWith('/diretor') ||
      location.pathname.startsWith('/ata')
    ) {
      newExpandedMenus.composicao = true;
    }
    
      if (
        location.pathname.startsWith('/escolas') ||
        location.pathname.startsWith('/usuarios') ||
        location.pathname.startsWith('/imagem') ||
        location.pathname.startsWith('/imagens') ||
        location.pathname.startsWith('/acoes') ||
        location.pathname.startsWith('/agencias') ||
        location.pathname.startsWith('/bancos') ||
        location.pathname.startsWith('/contas') ||
        location.pathname.startsWith('/programas') ||
        location.pathname.startsWith('/naturezas') ||
        location.pathname.startsWith('/tipos-bens') ||
        location.pathname.startsWith('/itens') ||
        location.pathname.startsWith('/tipo-pagamento')
      ) {
        newExpandedMenus.admin = true;
      }
    
    // if (location.pathname.startsWith('/dados-fnde')) {
    //   newExpandedMenus.consultas = true;
    // }

      const pddePaths = [
        '/receitas','/reprogramacoes','/despesas','/rendimentos',
        '/recursos-proprios','/devolucoes','/compensacoes','/redistribuicoes','/srea','/sres','/favorecidos'
      ];

      const saldosPaths = ['/extratos','/saldos','/saldo-anterior','/saldos-investimento','/saldos-conta'];

      const consultasFndePaths = ['/saldo-fnde','/saldo-fnde-gestao','/saldo-fnde-mensal','/dados-fnde','/comparativo-sres-saldo-fnde'];

      const ajustesPaths = ['/compensacoes','/devolucoes','/redistribuicoes','/recursos-proprios','/srea'];

      if (pddePaths.concat(saldosPaths).concat(consultasFndePaths).concat(ajustesPaths).some(p => location.pathname.startsWith(p))) {
        newExpandedMenus.pddeFinanceiro = true;
      }

      // Se estivermos em qualquer rota de Saldos, mantenha o submenu `saldos` expandido
      if (saldosPaths.some(p => location.pathname.startsWith(p))) {
        newExpandedMenus.saldos = true;
      }

      // Se estivermos em qualquer rota de Consultas FNDE, mantenha o submenu `consultasFnde` expandido
      if (consultasFndePaths.some(p => location.pathname.startsWith(p))) {
        newExpandedMenus.consultasFnde = true;
      }

      // Se estivermos em qualquer rota de Banco (agencias/bancos/contas), mantenha o submenu `banco` expandido
      if (['/agencias','/bancos','/contas'].some(p => location.pathname.startsWith(p))) {
        newExpandedMenus.banco = true;
      }

      // Se estivermos em qualquer rota de Ajustes, mantenha o submenu `ajustes` expandido
      if (ajustesPaths.some(p => location.pathname.startsWith(p))) {
        newExpandedMenus.ajustes = true;
      }
    
    setExpandedMenus(newExpandedMenus);
  }, [location.pathname]);

  return (
    <>
      {/* Backdrop para fechar a sidebar no mobile */}
      {isMobile && isSidebarOpen && (
        <div
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
        />
      )}
    <div
      ref={sidebarRef}
      className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
      style={{
        width: isMobile ? (isSidebarOpen ? '220px' : '0px') : (isSidebarOpen ? '220px' : '50px'),
        transition: 'width 0.3s ease-in-out',
        backgroundColor: '#198754',
        color: '#ffffff',
        padding: isMobile && !isSidebarOpen ? '0' : '5px',
        height: 'calc(100vh - 47px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: '48px',
        left: 0,
        zIndex: 1000,
        boxShadow: isMobile && !isSidebarOpen ? 'none' : '0 0 10px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Seção fixa: toggle + Home */}
      <div style={{ flexShrink: 0 }}>
        <button className={`btn w-100 d-flex ${isSidebarOpen ? 'justify-content-end' : 'justify-content-center'}`} onClick={toggleSidebar} style={{ color: '#ffffff', paddingBottom: '2px' }}>
          {isSidebarOpen ? <i className="bi bi-x"></i> : <i className="bi bi-list"></i>}
        </button>
        <ul className="list-unstyled mb-0">
          <li>
            <Link to="/home" className="nav-link" title={isSidebarOpen ? '' : 'Home'} style={{ color: '#ffffff' }}>
              <i className="bi bi-house me-2"></i>
              <span style={{ display: isSidebarOpen ? 'inline' : 'none' }}>Home</span>
            </Link>
          </li>
        </ul>
      </div>

      {/* Área rolável: restante do menu */}
      <div className="sidebar-body" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>

      {/* Menu */}
      <ul className="list-unstyled">

        {/* Admin Menu */}
        {(vinculo?.tipo === 'ADMIN' || user?.role === 'MASTER') && (
          <li>
            <button
              onClick={() => toggleMenu('admin')}
              title={isSidebarOpen ? '' : 'Administração'}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                width: '100%',
                textAlign: 'left',
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
              }}
              className="nav-link"
            >
              <i className="bi bi-gear me-2"></i>
              <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Administração</span>
                <i className={`bi ${expandedMenus.admin ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
              </span>
            </button>
            {expandedMenus.admin && isSidebarOpen && (
              <ul ref={adminSubmenuRef} className="list-unstyled ms-3 sidebar-submenu" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)', maxHeight: 'calc(100vh - 370px)', overflowY: 'auto' }}>
                <li><Link to="/acoes" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-list-check me-2"></i>Ações</Link></li>
                <li>
                  <button
                    onClick={() => toggleMenu('banco')}
                    title={isSidebarOpen ? '' : 'Banco'}
                    style={{ background: 'none', border: 'none', color: '#ffffff', width: '100%', textAlign: 'left', padding: '0.35rem 0.75rem', cursor: 'pointer' }}
                    className="nav-link small"
                  >
                    <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><i className="bi bi-bank me-2"></i>Banco</span>
                      <i className={`bi ${expandedMenus.banco ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
                    </span>
                  </button>
                  {expandedMenus.banco && isSidebarOpen && (
                    <ul ref={bancoSubmenuRef} className="list-unstyled ms-3 sidebar-submenu" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.08)', maxHeight: '220px', overflowY: 'auto' }}>
                      <li><Link to="/agencias" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-buildings me-2"></i>Agências</Link></li>
                      <li><Link to="/bancos" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-bank me-2"></i>Bancos</Link></li>
                      <li><Link to="/contas" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-credit-card me-2"></i>Contas Bancárias</Link></li>
                    </ul>
                  )}
                </li>
                {user?.role === 'MASTER' && (
                  <li>
                    <Link to="/escolas" title={isSidebarOpen ? '' : 'Escolas'} className="nav-link small" style={{ color: '#ffffff' }}>
                      <i className="bi bi-building me-2"></i>
                      Escolas
                    </Link>
                  </li>
                )}
                {/* Favorecidos foi movido para o menu Financeiro */}
                <li><Link to="/itens" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-card-list me-2"></i>Itens</Link></li>
                <li><Link to="/naturezas" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-tag me-2"></i>Naturezas</Link></li>
                <li><Link to="/programas" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-diagram-3 me-2"></i>Programas</Link></li>
                <li><Link to="/tipos-bens" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-box me-2"></i>Tipos de Bens</Link></li>
                {user?.role === 'MASTER' && (
                  <li>
                    <Link to="/usuarios" title={isSidebarOpen ? '' : 'Usuários'} className="nav-link small" style={{ color: '#ffffff' }}>
                      <i className="bi bi-people me-2"></i>
                      Usuários
                    </Link>
                  </li>
                )}
                {/* Templates page removed */}
                <li>
                  <Link to="/imagens" title={isSidebarOpen ? '' : 'Imagens'} className="nav-link small" style={{ color: '#ffffff' }}>
                    <i className="bi bi-images me-2"></i>
                    Imagens
                  </Link>
                </li>
                <li>
                  <Link to="/tipo-pagamento" title={isSidebarOpen ? '' : 'Tipos de Pagamento'} className="nav-link small" style={{ color: '#ffffff' }}>
                    <i className="bi bi-cash-coin me-2"></i>
                    Tipos de Pagamento
                  </Link>
                </li>
                <li>
                  <Link to="/sessions-ativas" title={isSidebarOpen ? '' : 'Sessões Ativas'} className="nav-link small" style={{ color: '#ffffff' }}>
                    <i className="bi bi-list-task me-2"></i>
                    Sessões Ativas
                  </Link>
                </li>

              </ul>
            )}
          </li>
        )}

        {/* Composição Menu */}
        <li>
          <button
            onClick={() => toggleMenu('composicao')}
            title={isSidebarOpen ? '' : 'Composição'}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              width: '100%',
              textAlign: 'left',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
            }}
            className="nav-link"
          >
            <i className="bi bi-folder me-2"></i>
            <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Cadastros</span>
              <i className={`bi ${expandedMenus.composicao ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
            </span>
          </button>
          {expandedMenus.composicao && isSidebarOpen && (
            <ul className="list-unstyled ms-3" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)' }}>
              <li>
                <Link to="/ata" title={isSidebarOpen ? '' : 'Ata da UEX'} className="nav-link small" style={{ color: '#ffffff' }}>
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Ata da UEX
                </Link>
              </li>
              {(user?.role === 'MASTER' || vinculo?.tipo === 'ADMIN') && (
                <li>
                  <Link to="/diretor" title={isSidebarOpen ? '' : 'Diretor'} className="nav-link small" style={{ color: '#ffffff' }}>
                    <i className="bi bi-person-badge me-2"></i>
                    Diretor
                  </Link>
                </li>
              )}
              <li>
                <Link to="/lista-compras" title={isSidebarOpen ? '' : 'Lista de Compras'} className="nav-link small" style={{ color: '#ffffff' }}>
                  <i className="bi bi-cart3 me-2"></i>
                  Lista de Compras
                </Link>
              </li>
              <li>
                <Link to="/pessoas" title={isSidebarOpen ? '' : 'Membros da UEX'} className="nav-link small" style={{ color: '#ffffff' }}>
                  <i className="bi bi-person me-2"></i>
                  Membros da UEX
                </Link>
              </li>
            </ul>
          )}
        </li>

        {/* PDDE Financeiro Menu */}
        <li>
          <button
            onClick={() => toggleMenu('pddeFinanceiro')}
            title={isSidebarOpen ? '' : 'Financeiro'}
            style={{ background: 'none', border: 'none', color: '#ffffff', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', cursor: 'pointer' }}
            className="nav-link"
          >
            <i className="bi bi-currency-dollar me-2"></i>
            <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Financeiro</span>
              <i className={`bi ${expandedMenus.pddeFinanceiro ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
            </span>
          </button>
          {expandedMenus.pddeFinanceiro && isSidebarOpen && (
            <ul ref={financeiroSubmenuRef} className="list-unstyled ms-3 sidebar-submenu" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)', maxHeight: 'calc(100vh - 370px)', overflowY: 'auto' }}>
                <>
                  <li>
                    <button
                      onClick={() => toggleMenu('ajustes')}
                      title={isSidebarOpen ? '' : 'Ajustes'}
                      style={{ background: isAjustesActive ? 'rgba(255,255,255,0.08)' : 'none', border: 'none', color: '#ffffff', width: '100%', textAlign: 'left', padding: '0.35rem 0.75rem', cursor: 'pointer' }}
                      className="nav-link small"
                    >
                      <i className="bi bi-sliders me-2"></i>
                      <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Ajustes</span>
                        <i className={`bi ${expandedMenus.ajustes ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
                      </span>
                    </button>
                    {expandedMenus.ajustes && isSidebarOpen && (
                      <ul className="list-unstyled ms-3" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)' }}>
                        <li><Link to="/compensacoes" className={`nav-link small ${isPathActive('/compensacoes') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-plus-slash-minus me-2"></i>Compensações</Link></li>
                        <li><Link to="/devolucoes" className={`nav-link small ${isPathActive('/devolucoes') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-arrow-return-left me-2"></i>Devoluções</Link></li>
                        <li><Link to="/redistribuicoes" className={`nav-link small ${isPathActive('/redistribuicoes') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-arrow-left-right me-2"></i>Redistribuições</Link></li>
                        <li><Link to="/recursos-proprios" className={`nav-link small ${isPathActive('/recursos-proprios') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-wallet2 me-2"></i>Recursos Próprios</Link></li>
                        <li><Link to="/srea" className={`nav-link small ${isPathActive('/srea') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-arrow-repeat me-2"></i>Saldo Reprogramado Exercício Anterior</Link></li>
                        <li><Link to="/srea-ajustes" className={`nav-link small ${isPathActive('/srea-ajustes') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-gear me-2"></i>Ajuste Saldo Reprogramado Exercício Anterior</Link></li>
                      </ul>
                    )}
                  </li>
                  <li><Link to="/despesas" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-receipt me-2"></i>Despesas</Link></li>
                  <li><Link to="/favorecidos" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-person-check me-2"></i>Favorecidos</Link></li>
                  <li><Link to="/receitas" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-cash-stack me-2"></i>Receitas</Link></li>
                  <li><Link to="/rendimentos" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-graph-up-arrow me-2"></i>Rendimentos</Link></li>
                  <li><Link to="/sres" className="nav-link small" style={{ color: '#ffffff' }}><i className="bi bi-arrow-repeat me-2"></i>Saldo Reprogramado Exercício Seguinte</Link></li>
                  <hr style={{ borderColor: 'rgba(255,255,255,0.2)', margin: '0.5rem 0' }} />
                  <li>
                    <button
                      onClick={() => toggleMenu('consultasFnde')}
                      title={isSidebarOpen ? '' : 'Consultas FNDE'}
                      style={{ background: (isConsultasFndeActive || isComparativoFndeActive) ? 'rgba(255,255,255,0.08)' : 'none', border: 'none', color: '#ffffff', width: '100%', textAlign: 'left', padding: '0.35rem 0.75rem', cursor: 'pointer' }}
                      className="nav-link small"
                    >
                      <i className="bi bi-search me-2"></i>
                      <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Consultas FNDE</span>
                        <i className={`bi ${expandedMenus.consultasFnde ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
                      </span>
                    </button>
                    {expandedMenus.consultasFnde && isSidebarOpen && (
                      <ul className="list-unstyled ms-3" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)' }}>
                        <li><Link to="/saldo-fnde" className={`nav-link small ${isPathActive('/saldo-fnde') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-bank me-2"></i>Consulta Saldos FNDE</Link></li>
                        <li><Link to="/saldo-fnde-gestao" className={`nav-link small ${isPathActive('/saldo-fnde-gestao') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-clipboard-data me-2"></i>Gestão Saldo FNDE</Link></li>
                        <li><Link to="/saldo-fnde-mensal" className={`nav-link small ${isPathActive('/saldo-fnde-mensal') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-calendar-month me-2"></i>Saldo FNDE Mensal</Link></li>
                        <li><Link to="/comparativo-sres-saldo-fnde" className={`nav-link small ${isPathActive('/comparativo-sres-saldo-fnde') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-bar-chart-line me-2"></i>Comparativo SRES x FNDE</Link></li>
                        <li><Link to="/dados-fnde" className={`nav-link small ${isPathActive('/dados-fnde') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-table me-2"></i>Situação de Atendimento</Link></li>
                      </ul>
                    )}
                  </li>
                  <li>
                    <button
                      onClick={() => toggleMenu('saldos')}
                      title={isSidebarOpen ? '' : 'Extratos e Saldos Bancários'}
                      style={{ background: isSaldosActive ? 'rgba(255,255,255,0.08)' : 'none', border: 'none', color: '#ffffff', width: '100%', textAlign: 'left', padding: '0.35rem 0.75rem', cursor: 'pointer' }}
                      className="nav-link small"
                    >
                      <i className="bi bi-bank2 me-2"></i>
                      <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Extratos e Saldos Bancários</span>
                        <i className={`bi ${expandedMenus.saldos ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
                      </span>
                    </button>
                    {expandedMenus.saldos && isSidebarOpen && (
                      <ul className="list-unstyled ms-3" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)' }}>
                        <li><Link to="/extratos" className={`nav-link small ${isPathActive('/extratos') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-journal-text me-2"></i>Extratos</Link></li>
                        <li><Link to="/saldo-anterior" className={`nav-link small ${isPathActive('/saldo-anterior') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-clock-history me-2"></i>Saldos Consolidados Exercício Anterior</Link></li>
                        <li><Link to="/saldos" className={`nav-link small ${isPathActive('/saldos') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-bank2 me-2"></i>Saldos Consolidados Exercício Atual</Link></li>
                        <li><Link to="/saldos-conta" className={`nav-link small ${isPathActive('/saldos-conta') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-bank2 me-2"></i>Saldos de Contas</Link></li>
                        <li><Link to="/saldos-investimento" className={`nav-link small ${isPathActive('/saldos-investimento') ? 'active' : ''}`} style={{ color: '#ffffff' }}><i className="bi bi-bank2 me-2"></i>Saldos de Investimentos</Link></li>
                      </ul>
                    )}
                  </li>
                </>
            </ul>
          )}
        </li>

        <li>
          <button
            onClick={() => toggleMenu('consultas')}
            title={isSidebarOpen ? '' : 'Consultas Externas'}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              width: '100%',
              textAlign: 'left',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
            }}
            className="nav-link"
          >
            <i className="bi bi-globe me-2"></i>
            <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Consultas Externas</span>
              <i className={`bi ${expandedMenus.consultas ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
            </span>
          </button>
          {expandedMenus.consultas && isSidebarOpen && (
            <ul className="list-unstyled ms-3" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)' }}>
              <li>
                  <button
                  onClick={e => {
                    e.preventDefault();
                    const escola = JSON.parse(localStorage.getItem('escola') || '{}');
                    const ano = localStorage.getItem('ano') || new Date().getFullYear();
                    const inep = escola.inep;
                    if (inep) {
                      const fndeUrl = `https://www.fnde.gov.br/pddeinfo/pddeinfo/escola/consultar?ano=${ano}&co_escola=${inep}&cnpj=&consultar=Consultar`;
                      window.open(fndeUrl, '_blank');
                    } else {
                      alert('INEP da escola não encontrado.');
                    }
                  }}
                  className="nav-link small"
                  title={isSidebarOpen ? '' : 'PDDE Rex'}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.35rem 0.75rem',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bi bi-box-arrow-up-right me-2"></i>
                  PDDE Rex
                </button>
              </li>
              <li>
                <button
                  onClick={e => {
                    e.preventDefault();
                    if (drivePath) {
                      window.open(drivePath, '_blank');
                    } else {
                      alert('Drive Path não cadastrado para esta escola.');
                    }
                  }}
                  className="nav-link small"
                  title={isSidebarOpen ? '' : 'Documentos'}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.35rem 0.75rem',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bi bi-hdd-network me-2"></i>
                  Documentos
                </button>
              </li>
              <li>
                <button
                  onClick={e => {
                    e.preventDefault();
                    const escola = JSON.parse(localStorage.getItem('escola') || '{}');
                    const cnpj = escola.cnpj;
                    if (cnpj) {
                      const cnpjUrl = `https://servicos.receita.fazenda.gov.br/Servicos/cnpjreva/Cnpjreva_Solicitacao.asp?cnpj=${cnpj}`;
                      window.open(cnpjUrl, '_blank');
                    } else {
                      alert('CNPJ da escola não encontrado.');
                    }
                  }}
                  className="nav-link small"
                  title={isSidebarOpen ? '' : 'Consulta CNPJ'}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.35rem 0.75rem',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bi bi-buildings me-2"></i>
                  Consulta CNPJ
                </button>
              </li>
              {/* Itens movidos para o menu Financeiro */}
            </ul>
          )}
        </li>

        {/* Relatórios Menu */}
        <li>
          <button
            onClick={() => toggleMenu('relatorios')}
            title={isSidebarOpen ? '' : 'Relatórios'}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              width: '100%',
              textAlign: 'left',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
            }}
            className="nav-link"
          >
            <i className="bi bi-file-earmark-bar-graph me-2"></i>
            <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Relatórios</span>
              <i className={`bi ${expandedMenus.relatorios ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ transition: 'transform 0.2s' }}></i>
            </span>
          </button>
          {expandedMenus.relatorios && isSidebarOpen && (
            <ul ref={relatoriosSubmenuRef} className="list-unstyled ms-3 sidebar-submenu" style={{ paddingLeft: '10px', borderLeft: '2px solid rgba(255,255,255,0.3)', maxHeight: 'calc(100vh - 370px)', overflowY: 'auto' }}>
              <li>
                <button
                  onClick={() => toggleMenu('prestacao')}
                  className="nav-link small"
                  title={isSidebarOpen ? '' : 'Prestação de Contas'}
                  style={{
                    color: '#ffffff',
                    background: 'none',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  <i className="bi bi-journal-check me-2"></i>
                  <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Prestação de Contas</span>
                    <i className={`bi ${expandedMenus.prestacao ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                  </span>
                </button>
                {expandedMenus.prestacao && isSidebarOpen && (
                  <ul className="list-unstyled ms-3" style={{ paddingLeft: '10px' }}>
                    <li>
                      <button
                        onClick={() => handleAbrirRelatorio('Demonstrativos')}
                        className="nav-link small"
                        style={{
                          color: '#ffffff',
                          background: 'none',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        <i className="bi bi-file-earmark-text me-2"></i>Demonstrativos por Programa
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleAbrirRelatorio('CapaPrograma')}
                        className="nav-link small"
                        style={{
                          color: '#ffffff',
                          background: 'none',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        <i className="bi bi-card-heading me-2"></i>Capa por Programa
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleAbrirRelatorio('CapaGeral')}
                        className="nav-link small"
                        style={{
                          color: '#ffffff',
                          background: 'none',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        <i className="bi bi-card-image me-2"></i>Capa Geral
                      </button>
                    </li>
                    <li>
                      <Link to="/atas-final" className="nav-link small" style={{ color: '#ffffff', display: 'block', padding: '0.5rem 1rem', textDecoration: 'none' }}>
                        <i className="bi bi-journal-text me-2"></i>Atas Finais
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
              <li>
                <button
                  onClick={() => toggleMenu('modelos')}
                  className="nav-link small"
                  title={isSidebarOpen ? '' : 'Modelos'}
                  style={{
                    color: '#ffffff',
                    background: 'none',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  <i className="bi bi-file-earmark-medical me-2"></i>
                  <span style={{ display: isSidebarOpen ? 'inline-flex' : 'none', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Modelos</span>
                    <i className={`bi ${expandedMenus.modelos ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                  </span>
                </button>
                {expandedMenus.modelos && isSidebarOpen && (
                  <ul className="list-unstyled ms-3" style={{ paddingLeft: '10px' }}>
                    <li>
                      <button
                        onClick={() => handleAbrirRelatorio('PlanPesqPrecos01')}
                        className="nav-link small"
                        style={{
                          color: '#ffffff',
                          background: 'none',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        <i className="bi bi-list-check me-2"></i>Planilha Pesquisa Preços 01
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleAbrirRelatorio('PlanPesqPrecos02')}
                        className="nav-link small"
                        style={{
                          color: '#ffffff',
                          background: 'none',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        <i className="bi bi-list-columns me-2"></i>Planilha Pesquisa Preços 02
                      </button>
                    </li>
                  </ul>
                )}
              </li>
              {/* Add more report types here as needed */}
              {(user?.role === 'MASTER' || vinculo?.tipo === 'ADMIN') && (
              <li>
                <Link
                  to="/relatorio-receita-por-cidade"
                  className="nav-link small"
                  style={{
                    color: '#ffffff',
                    padding: '0.5rem 1rem',
                    textDecoration: 'none'
                  }}
                >
                  <i className="bi bi-geo-alt me-2"></i>Receitas por Cidade
                </Link>
              </li>
              )}
            </ul>
          )}
          {showRelatorioModal && (
            <RelatorioFiltroModal
              show={showRelatorioModal}
              onClose={() => setShowRelatorioModal(false)}
              onConfirm={handleGerarRelatorio}
              relatorio={relatorioTipo}
              programas={programas}
            />
          )}
          {errorRelatorio && (
            <div className="alert alert-danger small m-2">{errorRelatorio}</div>
          )}
        </li>
      </ul>
      </div>{/* fim área rolável */}
    </div>
    </>
  );
};

export default Sidebar;